import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuthContext } from 'src/auth/hooks';
import { useActivityTracker } from './use-activity-tracker';

type SessionTimeoutOptions = {
  timeoutDuration?: number; // in milliseconds, default 4 hours
  warningDuration?: number; // in milliseconds, default 5 minutes before timeout
};

export const useSessionTimeout = ({
  timeoutDuration = 4 * 60 * 60 * 1000, // 4 hours
  warningDuration = 1 * 60 * 1000, // 1 minute
}: SessionTimeoutOptions = {}) => {
  const { logout, user } = useAuthContext();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleInactive = useCallback(() => {
    if (!user) return;
    
    // Clear any existing countdown interval before starting a new one
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    
    setShowWarning(true);
    setCountdown(warningDuration);
    
    // Start countdown
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1000) {
          // Time's up, logout user
          setShowWarning(false);
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          logout().then(() => {
            // Force page reload to ensure redirect to login
            window.location.reload();
          });
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);
  }, [user, logout, warningDuration]);

  const { resetActivity } = useActivityTracker({
    onInactive: handleInactive,
    timeoutDuration: timeoutDuration - warningDuration,
    checkInterval: 60000, // Check every minute
  });

  const extendSession = useCallback(() => {
    setShowWarning(false);
    setCountdown(0);
    
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    
    resetActivity();
  }, [resetActivity]);

  const logoutNow = useCallback(async () => {
    setShowWarning(false);
    setCountdown(0);
    
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    
    await logout();
    window.location.reload();
  }, [logout]);

  // Single cleanup effect
  useEffect(() => () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear warning when user logs out - simplified
  useEffect(() => {
    if (!user && showWarning) {
      setShowWarning(false);
      setCountdown(0);
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, showWarning]);

  return {
    showWarning,
    countdown,
    extendSession,
    logoutNow,
  };
};